import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Paper,
  Grid,
  Chip,
  Alert,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  CheckCircle,
  Home,
  ShoppingBag,
  Email,
  Phone,
  LocalShipping,
  Receipt,
  Star
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

function ThankYou() {
  const location = useLocation();
  const [orderNumber, setOrderNumber] = useState('');

  useEffect(() => {
    if (location.state?.orderNumber) {
      setOrderNumber(location.state.orderNumber);
    }
  }, [location.state]);

  const renderOrderDetails = () => (
    <Card sx={{ 
      mb: 3, 
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)'
    }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h6" component="h3" gutterBottom sx={{ 
          fontWeight: 600,
          mb: 3,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Receipt sx={{ mr: 1.5, fontSize: 28, color: 'primary.main' }} />
          Order Details
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Order Number
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ 
              fontSize: '1.125rem',
              letterSpacing: '0.5px'
            }}>
              {orderNumber || 'ORD-123456789'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Order Date
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ 
              fontSize: '1.125rem'
            }}>
              {new Date().toLocaleDateString()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Status
            </Typography>
            <Chip 
              label="Confirmed" 
              color="success" 
              size="medium"
              icon={<CheckCircle />}
              sx={{ 
                fontWeight: 600,
                py: 2
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
              Estimated Delivery
            </Typography>
            <Typography variant="body1" fontWeight="bold" sx={{ 
              fontSize: '1.125rem'
            }}>
              {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderNextSteps = () => (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)'
    }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h6" component="h3" gutterBottom sx={{ 
          fontWeight: 600,
          mb: 3,
          display: 'flex',
          alignItems: 'center'
        }}>
          <LocalShipping sx={{ mr: 1.5, fontSize: 28, color: 'primary.main' }} />
          What Happens Next?
        </Typography>
        
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}>
              1
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Order Confirmation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                You'll receive an email confirmation with your order details and tracking information.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}>
              2
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Production
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Our team will start creating your custom t-shirt with your selected design and customization.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}>
              3
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Shipping
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Once ready, your order will be shipped and you'll receive tracking updates via email.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: '50%', 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
            }}>
              4
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Delivery
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Your custom t-shirt will be delivered to your doorstep within the estimated timeframe.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderContactInfo = () => (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
    }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h6" component="h3" gutterBottom sx={{ 
          fontWeight: 600,
          mb: 2
        }}>
          Need Help?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          If you have any questions about your order, our customer support team is here to help.
        </Typography>

        <Stack spacing={2.5}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)'
          }}>
            <Email color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                Email Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                support@zarortshirts.com
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)'
          }}>
            <Phone color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                Phone Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                +1 (555) 123-4567
              </Typography>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)'
          }}>
            <Receipt color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                Order Reference
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please mention: {orderNumber || 'ORD-123456789'}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderActions = () => (
    <Paper sx={{ 
      p: { xs: 3, md: 4 }, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
    }}>
      <Stack 
        direction={{ xs: 'column', sm: 'column' }} 
        spacing={2} 
        justifyContent="center"
      >
        <Button
          variant="contained"
          startIcon={<Home />}
          component={Link}
          to="/"
          size="large"
          fullWidth
          sx={{ 
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          Continue Shopping
        </Button>
      </Stack>
    </Paper>
  );

  const renderReviewPrompt = () => (
    <Alert 
      severity="info" 
      sx={{ mt: 3 }}
      action={
        <Button color="inherit" size="small" startIcon={<Star />}>
          Leave Review
        </Button>
      }
    >
      <Typography variant="body2">
        Enjoyed your experience? Help others by leaving a review after you receive your order!
      </Typography>
    </Alert>
  );

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <MuiLink component={Link} to="/" color="inherit">
        Home
      </MuiLink>
      <Typography color="text.primary">
        Thank You
      </Typography>
    </Breadcrumbs>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
      {renderBreadcrumbs()}

      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 3, md: 5 },
        px: { xs: 1, md: 0 }
      }}>
        <CheckCircle sx={{ 
          fontSize: { xs: 60, md: 80 }, 
          color: 'success.main', 
          mb: 2,
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              opacity: 1
            },
            '50%': {
              transform: 'scale(1.05)',
              opacity: 0.9
            }
          }
        }} />
        <Typography variant="h3" component="h1" gutterBottom sx={{ 
          fontWeight: 700,
          fontSize: { xs: '2rem', md: '3rem' }
        }}>
          Thank You for Your Order!
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ 
          mb: 2,
          fontSize: { xs: '1rem', md: '1.25rem' }
        }}>
          Your custom t-shirt order has been confirmed and is being processed.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ 
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}>
          We're excited to create something special just for you!
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid item xs={12} lg={8}>
          {renderOrderDetails()}
          {renderNextSteps()}
          {renderContactInfo()}
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ 
            position: { xs: 'relative', lg: 'sticky' }, 
            top: { xs: 0, lg: 24 }
          }}>
            {renderActions()}
            {renderReviewPrompt()}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ThankYou;
